import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useMemo } from "react";

import { getSchemaHookRegistry } from "./schemaRegistry";
import type { ModuleDataValue } from "./types";

import { useAuth } from "@/lib/hooks/useAuth";

export interface SchemaIntegrationDataResult {
  data: Record<string, ModuleDataValue> | undefined;
  refetch: () => void;
  isLoading?: boolean;
}

/**
 * Generic hook to fetch integration-specific data for any integration module.
 * Resolves to integration-specific hook based on integrationName and moduleName.
 * 
 * @param integrationName - The integration name (e.g., "werkzoeken")
 * @param moduleName - The module name (e.g., "job-sync")
 * @param clientId - The client ID
 * @param tenantId - The tenant ID
 * @returns Integration-specific data object and refetch function
 */
export function useSchemaIntegrationData(
  integrationName: string | undefined,
  moduleName: string,
  clientId: string | undefined,
  tenantId: string | undefined
): SchemaIntegrationDataResult {
  const { tenantId: authTenantId } = useAuth();
  const effectiveTenantId = tenantId ?? authTenantId ?? undefined;
  
  // Get module registry and resolve hook
  const moduleRegistry = getSchemaHookRegistry(moduleName);
  const registeredHook = moduleRegistry?.getIntegrationDataHook?.(integrationName ?? "");
  
  // Create a no-op query that returns a disabled query if no registered hook exists
  // This ensures hooks are always called unconditionally
  const noOpQuery = useQuery<Record<string, ModuleDataValue>, AxiosError>({
    queryKey: ["no-op"],
    queryFn: () => Promise.resolve({} as Record<string, ModuleDataValue>),
    enabled: false,
  });
  
  // Call the registered hook if available, otherwise use no-op query
  const queryResult: UseQueryResult<Record<string, ModuleDataValue>, AxiosError> = registeredHook
    ? registeredHook({
      integrationName: integrationName ?? "",
      clientId,
      tenantId: effectiveTenantId,
    })
    : noOpQuery;
  
  // Return raw data - transformations should be handled by integration-specific hooks
  // IMPORTANT: Depend on queryResult.data specifically to detect when transformedData changes
  // When the integration-specific hook returns a new object with updated data, queryResult.data changes
  // This ensures we detect changes when comparison data arrives and updates transformedData
  const transformedData = useMemo(() => {
    // If no registered hook or query is disabled, return undefined
    if (!registeredHook || !queryResult.data) {
      return undefined;
    }
    
    // Return data as-is - integration-specific hooks handle transformations
    return queryResult.data;
  }, [registeredHook, queryResult.data]);
  
  return {
    data: transformedData,
    refetch: queryResult?.refetch ?? (() => {}),
    isLoading: queryResult.isLoading,
  };
}
